import { ActionIcon, Card, Group } from '@mantine/core';
import { useState } from 'react';
import type { DataEntryComponent } from '../../../../api/pages-client/pages-client.type';
import i18n from '../../../../base-dictionary';
import { baseBlack, skyDark } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { DeleteConfirmationModal } from '../../../data-management/components/delete-confirmation-modal';
import { getWidgetIcon } from '../../dashboards.helpers';
import { DataEntryTable } from './data-entry-table';
import { DataEntryWidgetModal } from './data-entry-widget-modal';

interface DataEntryWidgetProps {
  component: DataEntryComponent;
  onEdit: (component: DataEntryComponent) => void;
  onDelete: () => void;
  isEditingLayout?: boolean;
}

export const DataEntryWidget: React.FC<DataEntryWidgetProps> = ({
  component,
  onEdit,
  onDelete,
  isEditingLayout = false,
}) => {
  const widgetString = i18n.t('common.dashboard.widget');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleEdit = (updatedWidget: DataEntryComponent) => {
    onEdit(updatedWidget);
    setIsEditModalOpen(false);
  };

  const handleDelete = () => {
    onDelete();
    setIsDeleteModalOpen(false);
  };

  return (
    <>
      <Card
        key={`widget-${component.id}-${isEditingLayout ? 'editing' : 'normal'}`}
        shadow="md"
        radius="md"
        withBorder
        style={{
          height: '100%',
          maxWidth: '99%',
          ...(isEditingLayout
            ? {
                border: '2px dashed #1d75de',
                backgroundColor: 'rgba(29, 117, 222, 0.05)',
              }
            : {}),
        }}
      >
        {isEditingLayout && (
          <div
            // biome-ignore lint/a11y/noNoninteractiveTabindex: <explanation>
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                // react-grid-layout already treats focus + mousedown;
                // trigger a synthetic mouseDown to start the drag
                (e.currentTarget as HTMLElement).dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
              }
            }}
            className="draggable-handle"
            style={{
              height: '24px',
              backgroundColor: 'rgba(29, 117, 222, 0.2)',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              textAlign: 'center',
              cursor: 'move',
            }}
          >
            <Icon name="drag_indicator" size={20} color="#1d75de" />
          </div>
        )}

        <Card.Section inheritPadding py="sm">
          <Group justify="space-between">
            <Group gap={6}>
              <Icon name={getWidgetIcon('data_entry')} size={18} color={baseBlack} />
              <Text>{component.name}</Text>
              <Tooltip
                label={component.description || 'No Description Provided'}
                variant="black"
                multiline
                style={{ maxWidth: 350 }}
                zIndex={1000}
              >
                <ActionIcon variant="subtle" size="sm" radius="xl">
                  <Icon name="help_outline" size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            {!isEditingLayout && (
              <Group gap={4}>
                <Tooltip label={`Edit ${widgetString}`} variant="black">
                  <ActionIcon
                    variant="subtle"
                    onClick={() => setIsEditModalOpen(true)}
                    size="sm"
                    radius="xl"
                    color={skyDark}
                  >
                    <Icon name="edit" size={16} style={{ cursor: 'pointer' }} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={`Delete ${widgetString}`} variant="black">
                  <ActionIcon
                    variant="subtle"
                    onClick={() => setIsDeleteModalOpen(true)}
                    size="sm"
                    radius="xl"
                    color="red"
                  >
                    <Icon name="delete" size={16} style={{ cursor: 'pointer' }} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )}
          </Group>
        </Card.Section>

        <Card.Section inheritPadding py="sm" style={{ overflowY: 'auto' }}>
          <DataEntryTable component={component} onEdit={onEdit} isEditingLayout={isEditingLayout} />
        </Card.Section>
      </Card>

      <DataEntryWidgetModal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEdit}
        initialData={component}
        isEditing={true}
      />

      <DeleteConfirmationModal
        itemName={component.name}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDelete}
        title={`Delete ${widgetString}`}
        isDeleting={false}
        warning="This action will delete the entire widget and all its data entries. Measures using this data entry widget will no longer be able to access this data."
      />
    </>
  );
};
